import React, { useEffect, useState, useCallback } from 'react';
import { Card } from '../../components/ui/card';
import { TrendingUp, ShoppingBag, Users, Eye } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { api } from '../../api/client';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);

  const load = useCallback(() => {
    Promise.all([api.getStats(), api.listOrders({ limit: 5 }), api.listCustomers({ limit: 6 })])
      .then(([s, o, c]) => { setStats(s); setOrders(o || []); setCustomers(c || []); })
      .catch((err) => console.error('Dashboard load failed:', err));
  }, []);

  useEffect(() => { load(); }, [load]);

  const kpis = stats ? [
    { label: 'Revenue (paid)', value: '€' + stats.revenue.toLocaleString(), Icon: TrendingUp },
    { label: 'Total orders', value: stats.total_orders, Icon: ShoppingBag },
    { label: 'Customers', value: stats.total_customers, Icon: Users },
    { label: 'Conversion rate', value: stats.conversion + '%', Icon: Eye },
  ] : [];

  return (
    <div className="space-y-8">
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map(({ label, value, Icon }) => (
          <Card key={label} className="bg-zinc-950 border-zinc-900 p-5">
            <div className="flex items-center justify-between text-zinc-400 text-sm"><span>{label}</span><Icon className="h-4 w-4 text-[#d4af37]" /></div>
            <p className="text-3xl text-zinc-100 mt-2 serif-italic">{value}</p>
            <p className="text-emerald-400 text-xs mt-1">Live data</p>
          </Card>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="bg-zinc-950 border-zinc-900 p-6">
          <h3 className="serif-italic text-[#d4af37] text-xl mb-4">Recent Orders</h3>
          <div className="space-y-2">
            {orders.length === 0 && <p className="text-zinc-500 text-sm">No orders yet — place a test order from the storefront.</p>}
            {orders.map((o) => (
              <div key={o.id} className="flex items-center justify-between text-sm border-b border-zinc-900 py-2">
                <div>
                  <p className="text-zinc-100">{o.order_number} · <span className="text-zinc-400">{o.email}</span></p>
                  <p className="text-zinc-500 text-xs">{o.payment_method} · {(o.created_at||'').slice(0,10)}</p>
                </div>
                <div className="text-right">
                  <p className="text-[#d4af37]">€{(o.total||0).toFixed(2)}</p>
                  <Badge variant="outline" className={`text-xs ${o.status==='Paid'?'border-emerald-600 text-emerald-400':o.status==='Pending'?'border-amber-600 text-amber-400':'border-rose-600 text-rose-400'}`}>{o.status}</Badge>
                </div>
              </div>
            ))}
          </div>
        </Card>
        <Card className="bg-zinc-950 border-zinc-900 p-6">
          <h3 className="serif-italic text-[#d4af37] text-xl mb-4">CRM — Pipeline</h3>
          {stats && (
            <div className="grid grid-cols-3 gap-4 text-center mb-4">
              <div className="border border-zinc-900 rounded p-3"><p className="text-zinc-400 text-xs uppercase tracking-wider">Visitor</p><p className="serif-italic text-3xl text-[#d4af37]">{stats.visitors}</p></div>
              <div className="border border-zinc-900 rounded p-3"><p className="text-zinc-400 text-xs uppercase tracking-wider">Lead</p><p className="serif-italic text-3xl text-[#d4af37]">{stats.leads}</p></div>
              <div className="border border-zinc-900 rounded p-3"><p className="text-zinc-400 text-xs uppercase tracking-wider">Buyer</p><p className="serif-italic text-3xl text-[#d4af37]">{stats.buyers}</p></div>
            </div>
          )}
          <div className="space-y-2">
            {customers.slice(0, 5).map((c) => (
              <div key={c.id} className="flex items-center justify-between text-sm border-b border-zinc-900 py-2">
                <div><p className="text-zinc-100">{c.name}</p><p className="text-zinc-500 text-xs">{c.email} · {c.country || '—'}</p></div>
                <Badge variant="outline" className={`${c.stage==='Buyer'?'border-emerald-600 text-emerald-400':c.stage==='Lead'?'border-amber-600 text-amber-400':'border-zinc-700 text-zinc-400'}`}>{c.stage}</Badge>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card className="bg-zinc-950 border-zinc-900 p-6">
        <h3 className="serif-italic text-[#d4af37] text-xl mb-2">Tips for SEO success</h3>
        <p className="text-zinc-400 text-sm">Use the <span className="text-[#d4af37]">SEO</span> tab to fill product meta titles (≤60 chars), meta descriptions (≤160 chars), focus keyphrases (1–2 per page), alt text on all product images, and Open Graph image (1200×630). Each field has guided instructions — just like Yoast SEO Premium.</p>
      </Card>
    </div>
  );
};

export default Dashboard;
